import { useState } from 'react';
import { useAuth } from '@clerk/react';
import { ActionIcon, Menu } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconBan, IconDotsVertical, IconFlag } from '@tabler/icons-react';
import { useApiClient } from '../../api';
import ReportUserModal from './ReportUserModal';

// Overflow menu with the user-safety actions (contract §4.13 blocks, §4.14 reports).
//
// - Renders nothing when signed out (blocking/reporting need an identity).
// - `initialBlocked` seeds internal state on mount; undefined → treated as
//   not blocked. After that the component controls itself.
// - Block/unblock is optimistic (label flips immediately, reverts + notifies
//   on failure). `onBlockChange(nowBlocked)` fires after the server confirmed
//   (ConversationPage uses it to re-check can-message).
const UserActionsMenu = ({ userId, displayName, initialBlocked, onBlockChange, size = 'lg' }) => {
  const { isSignedIn } = useAuth();
  const api = useApiClient();
  const [blocked, setBlocked] = useState(!!initialBlocked);
  const [busy, setBusy] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);

  if (!isSignedIn) return null;

  const toggleBlock = async () => {
    if (busy) return;
    const next = !blocked;
    setBlocked(next); // optimistic
    setBusy(true);
    try {
      if (next) {
        await api.put(`/api/v1/me/blocks/${userId}`);
      } else {
        await api.del(`/api/v1/me/blocks/${userId}`);
      }
      onBlockChange?.(next);
    } catch (err) {
      setBlocked(!next); // revert
      notifications.show({
        title: 'Error',
        message: err?.message || `Could not ${next ? 'block' : 'unblock'} user`,
        color: 'red',
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Menu shadow="md" width={190} position="bottom-end" transitionProps={{ duration: 0 }}>
        <Menu.Target>
          <ActionIcon
            variant="subtle"
            color="gray"
            size={size}
            radius="xl"
            aria-label="User actions"
            title="User actions"
          >
            <IconDotsVertical size="1.25rem" />
          </ActionIcon>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Item
            leftSection={<IconBan size="1rem" />}
            onClick={toggleBlock}
            disabled={busy}
          >
            {blocked ? 'Unblock user' : 'Block user'}
          </Menu.Item>
          <Menu.Item
            color="red"
            leftSection={<IconFlag size="1rem" />}
            onClick={() => setReportOpen(true)}
          >
            Report user
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>

      <ReportUserModal
        opened={reportOpen}
        onClose={() => setReportOpen(false)}
        userId={userId}
        displayName={displayName}
      />
    </>
  );
};

export default UserActionsMenu;
